import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff, ArrowRight, ChevronLeft, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatApiErrorDetail } from '../lib/api';
import { FullScreenSpinner } from '../components/ProtectedRoute';

const BrandPanel = () => (
  <aside className="relative hidden lg:flex flex-col justify-between p-14 border-r border-white/5 bg-[#0d0c0a] overflow-hidden">
    <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#8B6F47]/10 blur-3xl" />
    <p className="relative font-body text-[11px] uppercase tracking-[0.3em] text-[#C9A961]/70">Buffet MultiEventos</p>
    <div className="relative animate-fade-up">
      <div className="w-14 h-px bg-[#C9A961] mb-8" />
      <h1 className="font-display text-5xl xl:text-6xl text-[#F5EDDD] leading-[1.05]">
        Os bastidores<br />de cada <em className="text-[#C9A961]">grande</em><br />celebração.
      </h1>
      <p className="font-body text-sm text-white/40 mt-8 max-w-sm leading-relaxed">
        Leads, calendário e preços do seu buffet em um só lugar — acesso exclusivo da equipe.
      </p>
    </div>
    <p className="relative font-body text-[11px] text-white/25">+30 anos · +500 eventos realizados</p>
  </aside>
);

const Field = ({ icon: Icon, children }) => (
  <div className="group flex items-center gap-3 border-b border-white/10 focus-within:border-[#C9A961] transition-colors duration-300 py-3">
    <Icon className="w-4 h-4 text-white/30 group-focus-within:text-[#C9A961] transition-colors duration-300" />
    {children}
  </div>
);

const inputCls = 'flex-1 bg-transparent font-body text-[15px] text-[#F5EDDD] placeholder:text-white/25 outline-none';

const LoginForm = ({ onSubmit, error, loading }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const submit = (e) => { e.preventDefault(); onSubmit(email, password); };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
      <Field icon={Mail}>
        <input data-testid="admin-login-email-input" type="email" required autoComplete="username" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" className={inputCls} />
      </Field>
      <Field icon={Lock}>
        <input data-testid="admin-login-password-input" type={show ? 'text' : 'password'} required autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Senha" className={inputCls} />
        <button type="button" data-testid="admin-login-toggle-password" onClick={() => setShow((s) => !s)}
          className="text-white/30 hover:text-[#C9A961] transition-colors" aria-label="Mostrar senha">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </Field>
      {error && (
        <p data-testid="admin-login-error" className="font-body text-[13px] text-[#e8a87c] bg-[#e8a87c]/5 border border-[#e8a87c]/20 rounded-sm px-4 py-3">
          {error}
        </p>
      )}
      <button type="submit" disabled={loading} data-testid="admin-login-submit-button"
        className="group w-full h-14 rounded-full bg-gradient-to-r from-[#8B6F47] via-[#C9A961] to-[#D4B483] text-[#1a1611] font-body text-sm font-semibold uppercase tracking-wider flex items-center justify-center gap-2 hover:shadow-[0_8px_30px_rgba(201,169,97,0.25)] active:scale-[0.98] transition-[transform,box-shadow,opacity] duration-300 disabled:opacity-60">
        {loading ? <span className="w-5 h-5 border-2 border-[#1a1611] border-t-transparent rounded-full animate-spin" /> : <>Entrar <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
      </button>
    </form>
  );
};

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const target = location.state?.from || '/admin';

  if (user === null) return <FullScreenSpinner />;
  if (user) return <Navigate to={target} replace />;

  const handleLogin = async (email, password) => {
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(target, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] grid lg:grid-cols-[1.1fr_1fr]" data-testid="admin-login-page">
      <BrandPanel />
      <main className="flex flex-col px-6 sm:px-12 lg:px-20 py-8">
        <Link to="/" data-testid="admin-login-back-link" className="flex items-center gap-1 font-body text-xs text-white/35 hover:text-[#C9A961] transition-colors w-fit">
          <ChevronLeft className="w-4 h-4" /> Voltar ao site
        </Link>
        <div className="flex-1 flex flex-col justify-center max-w-md w-full py-12 animate-fade-up">
          <div className="flex items-center gap-2 mb-6 text-[#C9A961]">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-body text-[11px] uppercase tracking-[0.25em]">Área restrita</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-[#F5EDDD] mb-3">Painel <span className="text-[#C9A961]">Admin</span></h2>
          <p className="font-body text-sm text-white/40 mb-12">Entre com seu e-mail e senha para continuar.</p>
          <LoginForm onSubmit={handleLogin} error={error} loading={loading} />
        </div>
      </main>
    </div>
  );
}
